/**
 * DI-3-S68 B1 (DI-D70) — every intake request has a deadline.
 *
 * Before, `inspection3Fetch` / `intakeFetch` called plain `fetch`: a request
 * the network swallowed (INC-13 — a row batch answered 499 after 50 s, 408
 * after 164 s) held the runner's one-request-at-a-time queue, and with it the
 * whole drop, for as long as the browser cared to wait. Now each request kind
 * carries ONE `AbortController` deadline:
 *
 *   ROW_BATCH_DEADLINE_MS          a data-points batch (≤ 500 rows / 900 kB)
 *   INTAKE_REQUEST_DEADLINE_MS     every other intake / inspection-3 call
 *
 * A request past its deadline is aborted and rejects with
 * `RequestTimeoutError` — a TRANSPORT failure, classified exactly as "the
 * server could not be reached" (it carries no HTTP status, so
 * `isIntakeFetchError` / `isInspection3FetchError` say no). The deadline
 * covers the answer's body too: a stall while the body streams is a stall.
 *
 * Not for the upload lane's PUTs (`upload/blockUpload.ts` has its own retry
 * and its own timing).
 */

/** Every intake / inspection-3 call but a row batch. */
export const INTAKE_REQUEST_DEADLINE_MS = 30_000;

/**
 * A data-points batch: up to 900 kB on the pilot's uplink. 90 s is the
 * PROPOSAL of story DI-3-S68 §4.2 — TO BE MEASURED (900 kB on a throttled
 * link) before it is fixed; it is the one number to change.
 */
export const ROW_BATCH_DEADLINE_MS = 90_000;

/** A request that did not answer within its deadline (a transport failure). */
export class RequestTimeoutError extends Error {
  readonly timeout = true as const;
  constructor(
    readonly path: string,
    readonly deadlineMs: number,
  ) {
    super(
      `request timed out after ${String(Math.round(deadlineMs / 1000))} s: ${path}`,
    );
    this.name = "RequestTimeoutError";
  }
}

export function isRequestTimeout(e: unknown): e is RequestTimeoutError {
  return (
    e instanceof Error && (e as Partial<RequestTimeoutError>).timeout === true
  );
}

/** "within 90 s" — the words a timeout's line uses. */
export function deadlineWords(e: RequestTimeoutError): string {
  return `did not answer within ${String(Math.round(e.deadlineMs / 1000))} s`;
}

export interface DeadlineTimers {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (id: unknown) => void;
}

const REAL_TIMERS: DeadlineTimers = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (id) => {
    globalThis.clearTimeout(id as ReturnType<typeof setTimeout>);
  },
};

/**
 * Run `run(signal)` under a deadline of `ms`: past it the signal aborts and
 * the promise rejects with `RequestTimeoutError` — even when the work does
 * not honour the signal (the race decides, not the fetch).
 */
export async function withDeadline<T>(
  path: string,
  ms: number,
  run: (signal: AbortSignal) => Promise<T>,
  timers: DeadlineTimers = REAL_TIMERS,
): Promise<T> {
  const ctrl = new AbortController();
  let id: unknown = null;
  const timeout = new Promise<never>((_, reject) => {
    id = timers.setTimeout(() => {
      reject(new RequestTimeoutError(path, ms));
      ctrl.abort();
    }, ms);
  });
  try {
    return await Promise.race([run(ctrl.signal), timeout]);
  } finally {
    timers.clearTimeout(id);
  }
}
